import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious,
} from "@/components/ui/pagination";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";

interface WalletDetail {
  id: string;
  userId: string;
  balance: number;
  updatedAt: string;
  user: { id: string; email: string; name: string; image?: string };
}

interface WalletTransaction {
  id: string;
  type: string;
  amount: number;
  balanceAfter: number;
  jobId?: string;
  stripePaymentIntentId?: string;
  description?: string;
  createdAt: string;
}

const fmtCurrency = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });

function getInitials(name: string): string {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

const TX_TYPE_STYLES: Record<string, { label: string; variant: "default" | "destructive" | "outline" | "secondary" }> = {
  TOP_UP: { label: "Top Up", variant: "default" },
  REFUND: { label: "Refund", variant: "secondary" },
  SUBSCRIPTION_CREDIT: { label: "Credit", variant: "secondary" },
  JOB_DEDUCTION: { label: "Deduction", variant: "destructive" },
  TRAINING_HOLD: { label: "Training hold", variant: "outline" },
  TRAINING_CONFIRMED: { label: "Training", variant: "destructive" },
  TRAINING_REFUND: { label: "Training refund", variant: "secondary" },
  ADMIN_DEBIT: { label: "Admin debit", variant: "destructive" },
  REFERRAL_CREDIT: { label: "Invite earnings", variant: "secondary" },
};

export const Route = createFileRoute("/dashboard/wallets/$userId")({
  component: WalletDetailPage,
});

function WalletDetailPage() {
  const navigate = useNavigate();
  const { userId } = Route.useParams();

  const [wallet, setWallet] = React.useState<WalletDetail | null>(null);
  const [transactions, setTransactions] = React.useState<WalletTransaction[]>([]);
  const [txTotal, setTxTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [txPage, setTxPage] = React.useState(1);
  const txLimit = 20;

  const [showAdjustDialog, setShowAdjustDialog] = React.useState(false);
  const [adjustAmount, setAdjustAmount] = React.useState("");
  const [adjustType, setAdjustType] = React.useState("REFUND");
  const [adjustReason, setAdjustReason] = React.useState("");
  const [adjusting, setAdjusting] = React.useState(false);

  React.useEffect(() => {
    if (!userId) return;
    const fetchWallet = async () => {
      try {
        const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/wallets/${userId}`, { credentials: "include" });
        if (!res.ok) throw new Error("Failed to fetch wallet");
        const json = await res.json();
        setWallet(json.data.wallet);
        setTransactions(json.data.transactions || []);
        setTxTotal(json.data.transactions?.length || 0);
        if (json.data.wallet?.user) {
          document.title = `${json.data.wallet.user.name || json.data.wallet.user.email} - Wallets - Admin`;
        }
      } catch (error) {
        console.error("Error fetching wallet:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchWallet();
  }, [userId]);

  const fetchTransactions = React.useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set("limit", txLimit.toString());
      params.set("offset", ((txPage - 1) * txLimit).toString());
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/wallets/${userId}/transactions?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const json = await res.json();
      setTransactions(json.data.transactions);
      setTxTotal(json.data.total);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  }, [userId, txPage]);

  React.useEffect(() => {
    if (!loading) fetchTransactions();
  }, [txPage, fetchTransactions, loading]);

  const handleAdjust = async () => {
    const amount = parseFloat(adjustAmount);
    if (!amount || amount === 0) { toast.error("Amount must be non-zero"); return; }
    if (!adjustReason.trim()) { toast.error("Reason is required"); return; }
    setAdjusting(true);
    try {
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/wallets/${userId}/adjust`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ amount, type: adjustType, reason: adjustReason }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to adjust balance");
      }
      const json = await res.json();
      setWallet((prev) => prev ? { ...prev, balance: json.data.newBalance } : prev);
      toast.success(`Balance adjusted to ${fmtCurrency(json.data.newBalance)}`);
      setShowAdjustDialog(false);
      setAdjustAmount("");
      setAdjustReason("");
      fetchTransactions();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to adjust balance");
    } finally {
      setAdjusting(false);
    }
  };

  if (loading) return (<div className="bg-background flex h-full items-center justify-center"><p className="text-muted-foreground">Loading wallet...</p></div>);
  if (!wallet) return (<div className="bg-background flex h-full items-center justify-center"><p className="text-muted-foreground">Wallet not found</p></div>);

  const txTotalPages = Math.ceil(txTotal / txLimit);

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-10 shrink-0 items-center gap-2 border-b px-3 md:h-12 md:px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/dashboard" className="text-xs">Dashboard</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbLink href="/dashboard/wallets" className="text-xs">Wallets</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage className="text-xs">{wallet.user.name || wallet.user.email}</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="border-border flex flex-col border-b px-3 py-3 md:px-4 md:py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12"><AvatarImage src={wallet.user.image || undefined} alt={wallet.user.name} /><AvatarFallback>{getInitials(wallet.user.name || wallet.user.email)}</AvatarFallback></Avatar>
              <div>
                <h1 className="text-lg font-bold tracking-tight md:text-xl">{wallet.user.name}</h1>
                <p className="text-muted-foreground text-sm">{wallet.user.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate({ to: "/dashboard/wallets" })}><ArrowLeft className="mr-1.5 h-3.5 w-3.5" />Back</Button>
              <Button size="sm" onClick={() => setShowAdjustDialog(true)}><Plus className="mr-1.5 h-3.5 w-3.5" />Adjust Balance</Button>
            </div>
          </div>
        </div>
      </header>
      <main className="space-y-6 px-3 py-4 md:px-4 max-w-5xl">
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Wallet</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg border p-3 space-y-1">
              <span className="text-muted-foreground text-xs">Balance</span>
              <p className="text-2xl font-bold">{fmtCurrency(wallet.balance)}</p>
            </div>
            <div className="rounded-lg border p-3 space-y-1">
              <span className="text-muted-foreground text-xs">Total Transactions</span>
              <p className="text-2xl font-bold">{txTotal}</p>
            </div>
            <div className="rounded-lg border p-3 space-y-1">
              <span className="text-muted-foreground text-xs">Last Updated</span>
              <p className="text-sm font-medium">{fmtDate(wallet.updatedAt)}</p>
            </div>
          </div>
        </section>
        <Separator />
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Transactions</h2>
          <div className="relative overflow-hidden rounded-lg border">
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance After</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Job ID</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No transactions</TableCell></TableRow>
                  ) : transactions.map((tx) => {
                    const style = TX_TYPE_STYLES[tx.type] || { label: tx.type, variant: "outline" as const };
                    const isPositive = tx.amount > 0;
                    return (
                      <TableRow key={tx.id}>
                        <TableCell><Badge variant={style.variant} className="text-xs">{style.label}</Badge></TableCell>
                        <TableCell className={`text-right font-medium ${isPositive ? "text-green-600" : "text-red-600"}`}>{isPositive ? "+" : ""}{fmtCurrency(tx.amount)}</TableCell>
                        <TableCell className="text-right">{fmtCurrency(tx.balanceAfter)}</TableCell>
                        <TableCell className="text-muted-foreground text-xs max-w-[200px] truncate">{tx.description || "-"}</TableCell>
                        <TableCell className="text-muted-foreground text-xs font-mono">{tx.jobId ? tx.jobId.slice(0, 8) + "..." : "-"}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">{fmtDate(tx.createdAt)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
          {txTotalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem><PaginationPrevious onClick={() => setTxPage(Math.max(1, txPage - 1))} className={txPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
                {Array.from({ length: Math.min(5, txTotalPages) }, (_, i) => {
                  let pn: number; if (txTotalPages <= 5) pn = i + 1; else if (txPage <= 3) pn = i + 1; else if (txPage >= txTotalPages - 2) pn = txTotalPages - 4 + i; else pn = txPage - 2 + i;
                  return (<PaginationItem key={pn}><PaginationLink onClick={() => setTxPage(pn)} isActive={txPage === pn} className="cursor-pointer">{pn}</PaginationLink></PaginationItem>);
                })}
                <PaginationItem><PaginationNext onClick={() => setTxPage(Math.min(txTotalPages, txPage + 1))} className={txPage === txTotalPages ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </section>
        <div className="h-8" />
      </main>
      <AlertDialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Adjust Balance</AlertDialogTitle>
            <AlertDialogDescription>Add funds to <strong>{wallet.user.name}</strong>&apos;s wallet. Current balance: {fmtCurrency(wallet.balance)}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="adjust-amount">Amount ($)</Label>
              <Input id="adjust-amount" type="number" step="0.01" placeholder="10.00" value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="adjust-type">Type</Label>
              <Select value={adjustType} onValueChange={setAdjustType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="REFUND">Refund</SelectItem>
                  <SelectItem value="SUBSCRIPTION_CREDIT">Subscription Credit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="adjust-reason">Reason</Label>
              <Textarea id="adjust-reason" placeholder="Reason for this adjustment..." value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} rows={3} />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={adjusting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleAdjust(); }} disabled={adjusting}>{adjusting ? "Adjusting..." : "Confirm"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
